<template>
  <div class="chess-tables" :class="`chess-tables--${tablesId}`">
    <div class="chess-tables__fixed">
      <template v-for="(table, tableIndex) in props.tables" :key="tableIndex">
        <div class="chess-tables__head">
          <div
            class="chess-tables__cell chess-tables__cell--head"
            :style="{ width: `${fixedWidth}px`, height: `${headCellHeight}px` }"
          >
            <template v-if="table.fixed">
              <div
                v-if="table.fixed.title"
                class="font-600 text-ellipsis"
                :title="table.fixed.title"
                v-html="table.fixed.title"
              />
              <div v-if="table.fixed.label" class="text-secondary text-ellipsis" v-html="table.fixed.label" />
            </template>
          </div>
        </div>
        <div
          v-for="(row, rowIndex) in table.rows"
          :key="rowIndex"
          class="chess-tables__cell"
          :style="{ width: `${fixedWidth}px`, height: `${cellHeight}px` }"
        >
          <chess-table-fixed-cell :fixed="row.fixed">
            <template v-if="slots.tooltip" #tooltip>
              <slot name="tooltip" :item="row" />
            </template>
          </chess-table-fixed-cell>
        </div>
      </template>
    </div>

    <vue-selecto
      v-if="props.isEdit"
      ref="selectoRef"
      :selectable-targets="[`.chess-tables--${tablesId} .selectable`]"
      :hit-rate="0"
      :ratio="0"
      :select-by-click="true"
      :scroll-options="scrollOptions"
      @select="handleSelectoSelect"
      @select-start="handleSelectoSelectStart"
      @select-end="handleSelectoSelectEnd"
      @scroll="handleSelectoScroll"
    />

    <recycle-scroller
      ref="recycleScrollerRef"
      class="chess-tables__scroll"
      :class="{ 'chess-tables__scroll--disabled': props.isEdit }"
      :style="{ width: `calc(100% - ${fixedWidth}px)` }"
      :items="daysWithId"
      :item-size="rowCellWidth"
      direction="horizontal"
      key-field="date"
      emit-update
      @update="onScrollerUpdate"
      @scroll-start="onScrollerUpdate"
      @scroll="onScrollerScroll"
      v-slot="{ item }"
      v-scroll.self="handleScroll"
    >
      <template v-for="(table, tableIndex) in props.tables" :key="tableIndex">
        <div class="chess-tables__head">
          <div
            class="chess-tables__cell chess-tables__cell--head chess-tables__cell--date"
            :class="{ 'chess-tables__cell--weekend': isWeekendDay(item.date) }"
            :style="{ height: `${headCellHeight}px` }"
          >
            <div class="ws-nw" :class="{ 'text-inactive': checkIsPastDay(item.date) }">
              {{ toDateString(item.date, 'YYYY-MM-DD', 'D MMM') }}
            </div>
            <div class="text-inactive text-capitalize">{{ toDateString(item.date, 'YYYY-MM-DD', 'dd') }}</div>
          </div>
        </div>

        <div
          v-for="(row, rowIndex) in table.rows"
          :key="rowIndex"
          class="chess-tables__cell"
          :style="{ width: `${rowCellWidth}px`, height: `${cellHeight}px` }"
        >
          <chess-date-cell
            ref="cellsRef"
            :key="`cell-${tableIndex}-${rowIndex}-${item.index}`"
            :id="`cell-${tableIndex}-${rowIndex}-${item.index}`"
            class="selectable"
            :period="props.period"
            :date="item.date"
            :value="row.cells[item.date]"
            :range="!!row.cells[item.date]?.rangeId"
            :first-in-range="row.cells[days[item.index - 1]]?.rangeId !== row.cells[item.date]?.rangeId"
            :last-in-range="row.cells[days[item.index + 1]]?.rangeId !== row.cells[item.date]?.rangeId"
            :range-inactive="rangeInactive(row, item)"
            :disabled="!props.isEdit"
            :variant="row.cells[item.date].variant"
            :chess-dates-available-from="props.chessDatesAvailableFrom"
          />
        </div>
      </template>
    </recycle-scroller>
  </div>
</template>

<script setup lang="ts" generic="T">
import dayjs from 'dayjs';
import { computed, onMounted, type Ref, ref, useId, useTemplateRef, watch } from 'vue';
import { VueSelecto } from 'vue3-selecto';
import type {
  ChessDateCellComponent,
  ChessDateCellType,
  ChessTableRowType,
  ChessTableType,
} from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';
import type { OnScroll, OnSelect } from 'selecto';
import {
  DEFAULT_CELL_HEIGHT,
  DEFAULT_FIXED_WIDTH,
  DEFAULT_HEAD_CELL_HEIGHT,
  DEFAULT_ROW_CELL_WIDTH,
} from '@/core/components/interface/Chess/Chess.const';
import ChessDateCell from '@/core/components/interface/Chess/ChessDateCell.vue';
import ChessTableFixedCell from '@/core/components/interface/Chess/ChessTableFixedCell.vue';
import { toDateString } from '@/core/utils/date';

const props = withDefaults(
  defineProps<{
    period?: RangeOfDateOnly;
    tables: ChessTableType<T>[];
    isEdit?: boolean;
    chessDatesAvailableFrom?: string; // Date in format YYYY-MM-DD
  }>(),
  {
    chessDatesAvailableFrom: dayjs().format('YYYY-MM-DD'),
  },
);

const emit = defineEmits<{
  (event: 'select', data: ChessDateCellType<T>[]): void;
}>();

const slots = defineSlots<{
  tooltip?: (props: { item: ChessTableRowType<T> }) => void;
}>();

const scrollProgress = defineModel<number>('scrollProgress', { required: true });

const tablesId = useId();

const recycleScrollerRef = ref();
const cellsRef = useTemplateRef('cellsRef');
let selectedCellsElements = new Set<HTMLElement>();
const selectedCells = ref([]) as Ref<ChessDateCellType<T>[]>;

const fixedWidth = computed(() => props.tables[0].fixedWidth || DEFAULT_FIXED_WIDTH);
const headCellHeight = computed(() => DEFAULT_HEAD_CELL_HEIGHT);
const rowCellWidth = computed(() => props.tables[0].rowCellWidth || DEFAULT_ROW_CELL_WIDTH);
const cellHeight = computed(() => props.tables[0].cellHeight || DEFAULT_CELL_HEIGHT);

const rangeInactive = (row: ChessTableRowType<T>, item: DayWithId) => {
  const date =
    !!row.cells[item.date]?.rangeId &&
    Object.values(row.cells).findLast((i) => i.rangeId === row.cells[item.date]?.rangeId)?.date;

  return !!date && date < props.chessDatesAvailableFrom;
};

const days = computed(() => {
  if (!props.period) {
    return [];
  }

  const days = [];
  let dateStart = dayjs(props.period.from);
  const dateEnd = dayjs(props.period.to);

  while (dateEnd.diff(dateStart, 'days') >= 0) {
    days.push(dateStart.format('YYYY-MM-DD'));
    dateStart = dateStart.add(1, 'day');
  }

  return days;
});

type DayWithId = {
  index: number;
  date: string;
};
const selectoRef = useTemplateRef<VueSelecto>('selectoRef');

const onScrollerScroll = () => {
  selectoRef.value?.checkScroll();
};

const onScrollerUpdate = () => {
  if (!Array.isArray(cellsRef.value)) return;

  selectoRef.value?.findSelectableTargets();
};

const daysWithId = computed<DayWithId[]>(() => days.value.map((date, index) => ({ index, date })));

let updateRecycleScrollerTimerId: NodeJS.Timeout;

watch(
  () => scrollProgress.value,
  (value) => {
    recycleScrollerRef.value.$el.scrollTo({
      left: ((recycleScrollerRef.value.$el.scrollWidth - recycleScrollerRef.value.$el.clientWidth) * value) / 100,
    });

    /*
     * Обновляем элементы вирт. скролла, потому что при открытии модальных окон дизеблится страница, что вызывает обновление элементов скролла.
     * Эти элементы уже выбраны и обновление вирт. скролла их ломает.
     * setTimeout 400 потому что сам компонент recycle-scroller производит обновление элементов через 300ms
     * */
    clearTimeout(updateRecycleScrollerTimerId);
    updateRecycleScrollerTimerId = setTimeout(() => {
      if (recycleScrollerRef.value) {
        recycleScrollerRef.value.updateVisibleItems();
      }
    }, 400);
  },
);

watch(
  () => selectedCells.value,
  () => {
    emit('select', selectedCells.value);
  },
  { deep: true },
);

onMounted(() => {
  setTimeout(() => {
    scrollTo(props.chessDatesAvailableFrom);
  });
});

const isWeekendDay = (date: string) => dayjs(date).isoWeekday() > 5;

const checkIsPastDay = (day: string) => {
  return day < props.chessDatesAvailableFrom;
};

const scrollOptions = {
  container: () => recycleScrollerRef.value.$el,
  throttleTime: 30,
  threshold: 70,
};

const handleSelectoSelect = (eventData: OnSelect) => {
  if (!Array.isArray(cellsRef.value)) return;

  for (const element of eventData.selected) {
    // filter потому что recycle-scroller может задублировать элементы и одинаковых ячеек будет 2
    const cells = cellsRef.value.filter((item) => item.$el.id === element.id);

    cells.forEach((cell) => {
      cell.toggleIsSelected(true);
      selectedCellsElements.add(cell.$el);
    });
  }

  for (const element of eventData.removed) {
    // filter потому что recycle-scroller может задублировать элементы и одинаковых ячеек будет 2
    const cells = cellsRef.value.filter((item) => item.$el.id === element.id);

    cells.forEach((cell) => {
      cell.toggleIsSelected(false);

      selectedCellsElements = new Set([...selectedCellsElements].filter((item) => item.id !== cell.$el.id));
    });
  }
};

const handleSelectoScroll = (eventData: OnScroll) => {
  const direction = eventData.direction;

  recycleScrollerRef.value.$el.scrollTo({
    left: recycleScrollerRef.value.$el.scrollLeft + direction[0] * 16,
  });
};

const handleSelectoSelectStart = () => {
  selectedCells.value.splice(0, selectedCells.value.length); // Очищает массив без emit
  selectedCellsElements.clear();
};

const handleSelectoSelectEnd = () => {
  if (!Array.isArray(cellsRef.value)) return;

  const rangeId = [...selectedCellsElements]
    .find((item) => item.hasAttribute('data-range-id'))
    ?.getAttribute('data-range-id');

  if (rangeId) {
    const cells = cellsRef.value.filter((item) => item.$el.getAttribute('data-range-id') === rangeId);

    cells.forEach((cell) => {
      const cellValue = cell.selectCell();
      if (cellValue) {
        selectedCells.value.push(cellValue);
      }

      if (cell.$el.getAttribute('data-selected') !== 'true') {
        cell.toggleIsSelected(true);
      }
    });
  } else {
    for (const element of [...selectedCellsElements]) {
      const cell = cellsRef.value.find((item) => item.$el.id === element.id);

      if (cell) {
        const cellValue = cell.selectCell();
        if (cellValue) {
          selectedCells.value.push(cellValue);
        }
      }
    }
  }

  // clear selecto for fix bug with double selection LKP-3628
  if (selectoRef.value) {
    selectoRef.value.setSelectedTargets([]);
  }
};

const scrollTo = (date: string) => {
  setTimeout(() => {
    if (!recycleScrollerRef.value) {
      return;
    }

    const dateIndex = daysWithId.value.findIndex((item) => item.date === date);

    if (~dateIndex && recycleScrollerRef.value) {
      recycleScrollerRef.value.$el.scrollLeft = dateIndex * rowCellWidth.value;
    }
  });
};

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement;
  scrollProgress.value = (target.scrollLeft / (target.scrollWidth - target.clientWidth)) * 100;
};

defineExpose({
  unselectAllCells: () => {
    if (!Array.isArray(cellsRef.value)) return;

    cellsRef.value.forEach((cell) => {
      if (cell.$el.getAttribute('data-selected') === 'true') {
        cell.toggleIsSelected(false);
      }
    });

    selectedCells.value = [];
    selectedCellsElements.clear();
  },
  scrollTo,
});
</script>

<style scoped lang="scss">
.chess-tables {
  $root: &;

  display: flex;

  font-size: 12px;
  line-height: 16px;

  &__fixed {
    #{$root}__head {
      padding-right: 8px;
    }
  }

  &__scroll {
    &--disabled {
      overflow: hidden !important;
    }
  }

  &__head {
    border-bottom: 1px solid $border-color-light;
  }

  &__cell {
    padding: 2px;

    font-weight: 500;

    border-radius: 8px;

    &--head {
      display: flex;
      flex-direction: column;
      justify-content: center;

      padding: 10px;
    }

    &--date {
      text-align: center;
    }

    &--weekend {
      font-weight: 700;
    }
  }
}
</style>
